import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as XLSX from "xlsx";
import { Stack, HStack, StackItem } from "@astryxdesign/core/Stack";
import { Grid } from "@astryxdesign/core/Grid";
import { Text, Heading } from "@astryxdesign/core/Text";
import { Card } from "@astryxdesign/core/Card";
import { SelectableCard } from "@astryxdesign/core/SelectableCard";
import { Button } from "@astryxdesign/core/Button";
import { Badge } from "@astryxdesign/core/Badge";
import { Banner } from "@astryxdesign/core/Banner";
import { Selector } from "@astryxdesign/core/Selector";
import { MultiSelector } from "@astryxdesign/core/MultiSelector";
import { TextInput } from "@astryxdesign/core/TextInput";
import { DateInput } from "@astryxdesign/core/DateInput";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Table, proportional, pixel } from "@astryxdesign/core/Table";
import { reportsAPI } from "../../services/api";
import AstryxTheme from "../../theme/AstryxTheme";
import "../../fontawesome";

const TIPO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "prestada", label: "Prestados" },
  { value: "tomada", label: "Tomados" },
];

const GRUPO_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "assescont", label: "Asescont" },
  { value: "comsind", label: "Comsind" },
  { value: "advisa", label: "Advisa" },
];

const STATUS_NOTA_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "cancelled", label: "Cancelada" },
  { value: "substituted", label: "Substituída" },
];

// ... (Funções auxiliares permanecem idênticas para manter a funcionalidade)
function competenciaMmAaaaToApi(value) {
  const s = (value || "").trim().replace(/\s/g, "");
  const match = s.match(/^(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = parseInt(match[1], 10);
  const year = parseInt(match[2], 10);
  if (month < 1 || month > 12 || year < 1900 || year > 2100) return null;
  return `${year}-${String(month).padStart(2, "0")}`;
}

// "mm/aaaa" → "MMYYYY" (formato do endpoint integridade-coleta), null se inválido.
function competenciaMmAaaaToMmYyyy(value) {
  const s = (value || "").trim().replace(/\s/g, "");
  const match = s.match(/^(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = parseInt(match[1], 10);
  const year = parseInt(match[2], 10);
  if (month < 1 || month > 12 || year < 1900 || year > 2100) return null;
  return `${String(month).padStart(2, "0")}${year}`;
}

function formatMoney(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Número puro para Excel (somas e formatação local da planilha). Célula vazia se inválido. */
function excelNumeric(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : "";
}

/** Número da nota como número no Excel; mantém texto se não for puramente numérico. */
function excelNumeroNota(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  const n = Number(s);
  return Number.isFinite(n) && s === String(n) ? n : s;
}

function formatMonthYear(dateValue) {
  if (!dateValue) return "";
  const s = String(dateValue).trim();
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  if (dateOnly) {
    const [, y, mo] = dateOnly;
    return `${mo}/${y}`;
  }
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${year}`;
}

/** dd/mm/aaaa a partir de data-only (YYYY-MM-DD) ou ISO, sem deslocar um dia por interpretação UTC. */
function formatCalendarDatePtBr(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return `${d}/${m}/${y}`;
  }
  const t = new Date(s);
  if (Number.isNaN(t.getTime())) return "";
  return t.toLocaleDateString("pt-BR");
}

function downloadConferenciaHubPortalExcel(data) {
  const rows = [
    [
      "Código",
      "Empresa",
      "CNPJ",
      "Notas no HUB",
      "Número de notas no Portal",
      "Diferença (HUB − Portal)",
      "Job download (referência)",
    ],
    ...data.companies.map((r) => [
      r.code ?? "—",
      r.name ?? "—",
      r.cnpj ?? "—",
      r.notasHub ?? 0,
      r.notasPortal != null ? r.notasPortal : "—",
      r.diferenca != null ? r.diferenca : "—",
      r.downloadJobId ?? "—",
    ]),
    [],
    [
      "Total",
      "",
      "",
      data.totalHub ?? 0,
      data.totalPortal != null ? data.totalPortal : "—",
      data.totalPortal != null ? (data.totalHub ?? 0) - data.totalPortal : "—",
      "",
    ],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Hub x Portal");
  const from = (data.dateFrom || "").replace(/-/g, "");
  const to = (data.dateTo || "").replace(/-/g, "");
  const filename = `Conferencia_Hub_Portal_${from}_${to}.xlsx`;
  XLSX.writeFile(wb, filename);
}

function downloadNumeroNotasExcel(data, competenciaApi) {
  const rows = [
    ["Código", "Empresa", "Tomados", "Prestados"],
    ...data.companies.map((r) => [
      r.code ?? "—",
      r.name ?? "—",
      r.tomados,
      r.prestados,
    ]),
    [],
    ["Total", "", data.totalTomados, data.totalPrestados],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Notas por empresa");
  const filename = `Numero_notas_competencia_${competenciaApi.replace("-", "_")}.xlsx`;
  XLSX.writeFile(wb, filename);
}

function downloadValorLiquidoExcel(data, competenciaApi) {
  const rows = [
    ["Código", "Empresa", "Quantidade", "Valor líquido total"],
    ...data.companies.map((r) => [
      r.code ?? "—",
      r.name ?? "—",
      r.count,
      formatMoney(r.totalNetValue),
    ]),
    [],
    ["Total", "", data.totalNotes, formatMoney(data.totalNetValue)],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Valor líquido por empresa");
  const filename = `Valor_liquido_competencia_${competenciaApi.replace("-", "_")}.xlsx`;
  XLSX.writeFile(wb, filename);
}

function downloadRelacaoRetencoesExcel(
  data,
  competenciaInicialApi,
  competenciaFinalApi,
) {
  const headers = [
    "Código Empresa",
    "Empresa",
    "Competência",
    "Numero da Nota",
    "Data de Emissao",
    "Access_Key (Chave de acesso)",
    "CNPJ Prestador",
    "Nome Prestador",
    "Tomador",
    "Valor Total",
    "PIS Retido",
    "COFINS Retido",
    "ISS Retido",
    "ISS Proprio",
    "INSS Retido",
    "IRRF",
    "CSRF",
    "Valor Liquido",
    "Status",
    "Tributacao Nacional",
  ];
  const dataRows = data.notes.map((n) => {
    const emissaoStr = formatCalendarDatePtBr(n.dataEmissao);
    return [
      n.companyCode ?? "",
      n.companyName ?? "",
      n.competenciaMmAaaa ?? "",
      excelNumeroNota(n.numeroNota),
      emissaoStr,
      n.accessKey ?? "",
      n.providerCnpj ?? "",
      n.providerName ?? "",
      n.tomador ?? "",
      excelNumeric(n.valorTotal),
      excelNumeric(n.pisRetido),
      excelNumeric(n.cofinsRetido),
      excelNumeric(n.issRetido),
      excelNumeric(n.issProprio),
      excelNumeric(n.inssRetido),
      excelNumeric(n.irrfRetido),
      excelNumeric(n.csrfRetido),
      excelNumeric(n.valorLiquido),
      n.status ?? "",
      n.tributacaoNacional ?? "",
    ];
  });
  const totals = data.notes.reduce(
    (acc, n) => ({
      valorTotal: acc.valorTotal + (Number(n.valorTotal) || 0),
      pisRetido: acc.pisRetido + (Number(n.pisRetido) || 0),
      cofinsRetido: acc.cofinsRetido + (Number(n.cofinsRetido) || 0),
      issRetido: acc.issRetido + (Number(n.issRetido) || 0),
      issProprio: acc.issProprio + (Number(n.issProprio) || 0),
      inssRetido: acc.inssRetido + (Number(n.inssRetido) || 0),
      irrfRetido: acc.irrfRetido + (Number(n.irrfRetido) || 0),
      csrfRetido: acc.csrfRetido + (Number(n.csrfRetido) || 0),
      valorLiquido: acc.valorLiquido + (Number(n.valorLiquido) || 0),
    }),
    {
      valorTotal: 0,
      pisRetido: 0,
      cofinsRetido: 0,
      issRetido: 0,
      issProprio: 0,
      inssRetido: 0,
      irrfRetido: 0,
      csrfRetido: 0,
      valorLiquido: 0,
    },
  );
  const totalRow = [
    "Total",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    totals.valorTotal,
    totals.pisRetido,
    totals.cofinsRetido,
    totals.issRetido,
    totals.issProprio,
    totals.inssRetido,
    totals.irrfRetido,
    totals.csrfRetido,
    totals.valorLiquido,
    "",
    "",
  ];
  const rows = [headers, ...dataRows, [], totalRow];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Relação de Retenções");
  const filename = `Relacao_Retencoes_competencias_${competenciaInicialApi.replace(
    "-",
    "_",
  )}_a_${competenciaFinalApi.replace("-", "_")}.xlsx`;
  XLSX.writeFile(wb, filename);
}

function downloadRelacaoRetencoesPorGrupoExcel(
  data,
  competenciaInicialApi,
  competenciaFinalApi,
) {
  const headers = [
    "Grupo",
    "Código Empresa",
    "Empresa",
    "Competência",
    "Numero da Nota",
    "Data de Emissao",
    "Access_Key (Chave de acesso)",
    "CNPJ Prestador",
    "Nome Prestador",
    "Tomador",
    "Valor Total",
    "PIS Retido",
    "COFINS Retido",
    "ISS Retido",
    "ISS Proprio",
    "INSS Retido",
    "IRRF",
    "CSRF",
    "Valor Liquido",
    "Status",
    "Tributacao Nacional",
  ];

  const dataRows = data.notes.map((n) => [
    n.companyGroup ?? "Outros",
    n.companyCode ?? "",
    n.companyName ?? "",
    n.competenciaMmAaaa ?? "",
    n.numeroNota ?? "",
    formatCalendarDatePtBr(n.dataEmissao),
    n.accessKey ?? "",
    n.providerCnpj ?? "",
    n.providerName ?? "",
    n.tomador ?? "",
    formatMoney(n.valorTotal),
    formatMoney(n.pisRetido),
    formatMoney(n.cofinsRetido),
    formatMoney(n.issRetido),
    formatMoney(n.issProprio),
    formatMoney(n.inssRetido),
    formatMoney(n.irrfRetido),
    formatMoney(n.csrfRetido),
    formatMoney(n.valorLiquido),
    n.status ?? "",
    n.tributacaoNacional ?? "",
  ]);

  const groupOrder = ["Asescont", "Comsind", "Advisa", "Outros"];
  const groupTotalsRows = groupOrder
    .filter((g) => data.groupTotals && data.groupTotals[g])
    .map((g) => {
      const t = data.groupTotals[g];
      return [
        `Total ${g}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        formatMoney(t.valorTotal),
        formatMoney(t.pisRetido),
        formatMoney(t.cofinsRetido),
        formatMoney(t.issRetido),
        formatMoney(t.issProprio),
        formatMoney(t.inssRetido),
        formatMoney(t.irrfRetido),
        formatMoney(t.csrfRetido),
        formatMoney(t.valorLiquido),
        "",
        "",
      ];
    });

  // Total geral (somando os totais do grupo)
  const grandTotals = groupOrder.reduce(
    (acc, g) => {
      const t = data.groupTotals?.[g];
      if (!t) return acc;
      acc.valorTotal += t.valorTotal || 0;
      acc.pisRetido += t.pisRetido || 0;
      acc.cofinsRetido += t.cofinsRetido || 0;
      acc.issRetido += t.issRetido || 0;
      acc.issProprio += t.issProprio || 0;
      acc.inssRetido += t.inssRetido || 0;
      acc.irrfRetido += t.irrfRetido || 0;
      acc.csrfRetido += t.csrfRetido || 0;
      acc.valorLiquido += t.valorLiquido || 0;
      return acc;
    },
    {
      valorTotal: 0,
      pisRetido: 0,
      cofinsRetido: 0,
      issRetido: 0,
      issProprio: 0,
      inssRetido: 0,
      irrfRetido: 0,
      csrfRetido: 0,
      valorLiquido: 0,
    },
  );

  const grandTotalRow = [
    "Total Geral",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    formatMoney(grandTotals.valorTotal),
    formatMoney(grandTotals.pisRetido),
    formatMoney(grandTotals.cofinsRetido),
    formatMoney(grandTotals.issRetido),
    formatMoney(grandTotals.issProprio),
    formatMoney(grandTotals.inssRetido),
    formatMoney(grandTotals.irrfRetido),
    formatMoney(grandTotals.csrfRetido),
    formatMoney(grandTotals.valorLiquido),
    "",
    "",
  ];

  const rows = [
    headers,
    ...dataRows,
    [],
    ...groupTotalsRows.flatMap((r) => [r, []]),
    grandTotalRow,
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Retenções por Grupo");

  const filename = `Retencoes_por_grupo_${competenciaInicialApi.replace(
    "-",
    "_",
  )}_a_${competenciaFinalApi.replace("-", "_")}.xlsx`;
  XLSX.writeFile(wb, filename);
}

function downloadConferenciaChavesExcel(
  data,
  competenciaInicialApi,
  competenciaFinalApi,
) {
  const headers = [
    "Codigo",
    "Empresa",
    "Regime tributário",
    "Chave de acesso",
    "Tipo nota",
    "Data de emissão",
    "Competência",
    "Número da nota",
    "Valor da nota",
    "Status da nota",
    "Situacao",
    "Empresa correta (Hub)",
  ];
  const dataRows = (data.rows || []).map((r) => [
    r.companyCode ?? "",
    r.companyName ?? "",
    r.regimeTributario ?? "",
    r.accessKey ?? "",
    r.noteType ?? "",
    formatCalendarDatePtBr(r.issueDate),
    formatCalendarDatePtBr(r.competenceDate),
    r.numeroNota ?? "",
    r.valorNota != null && r.valorNota !== "" ? formatMoney(r.valorNota) : "",
    r.noteStatus ?? "",
    r.situacao ?? "",
    r.empresaCorreta ?? "",
  ]);
  const sum = data.summary || {};
  const summaryRow = [
    "Resumo",
    "Importadas: " + (sum.importadas ?? 0),
    "Não importadas: " + (sum.naoImportadas ?? 0),
    "Importadas incorretamente: " + (sum.importadasIncorretamente ?? 0),
    "Somente Domínio: " + (sum.somenteDominio ?? 0),
    "Competência divergente: " + (sum.competenciaDivergente ?? 0),
    "",
    "",
    "",
    "",
    "",
    "",
  ];
  const totalValorRow = [
    "Total valor (notas Hub)",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    formatMoney(sum.totalValorHub ?? 0),
    "",
    "",
    "",
  ];
  const rows = [headers, ...dataRows, [], summaryRow, totalValorRow];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Conferencia chaves");
  const filename = `Conferencia_chaves_Hub_Dominio_${competenciaInicialApi.replace(
    "-",
    "_",
  )}_a_${competenciaFinalApi.replace("-", "_")}.xlsx`;
  XLSX.writeFile(wb, filename);
}

function downloadRelacaoNotasDivergentesExcel(data) {
  const headers = [
    "Código",
    "Empresa",
    "Número da Nota",
    "Data de Emissão",
    "Data de Competência",
    "Access_Key",
    "Tomador",
    "Valor Total",
    "Valor Líquido",
    "Status",
  ];
  const rows = [
    headers,
    ...data.notes.map((n) => [
      n.companyCode ?? "—",
      n.companyName ?? "—",
      n.numeroNota ?? "",
      formatCalendarDatePtBr(n.dataEmissao),
      formatMonthYear(n.dataCompetencia),
      n.accessKey ?? "",
      n.tomador ?? "",
      formatMoney(n.valorTotal),
      formatMoney(n.valorLiquido),
      n.status ?? "",
    ]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Notas Divergentes");
  const filename =
    `Relacao_Notas_Divergentes_${data.dateFrom}_a_${data.dateTo}.xlsx`.replace(
      /-/g,
      "_",
    );
  XLSX.writeFile(wb, filename);
}

function downloadEmpresasNaoCadastradasExcel(data) {
  const headers = ["Código (codi_emp)", "CNPJ", "Apelido (Domínio)"];
  const dataRows = (data.empresas || []).map((e) => [
    e.code ?? "",
    e.cnpj ?? "",
    e.apelido ?? "",
  ]);
  const summaryRows = [
    [],
    ["Total empresas ativas no Domínio (Betha)", data.totalBetha ?? 0],
    ["Total empresas cadastradas no Hub NFSe", data.totalHub ?? 0],
    ["Total NÃO cadastradas", data.totalNaoCadastradas ?? 0],
  ];
  const rows = [headers, ...dataRows, ...summaryRows];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Empresas não cadastradas");
  const filename = `Empresas_nao_cadastradas_Hub.xlsx`;
  XLSX.writeFile(wb, filename);
}

function downloadEmpresasProblemasAcessoExcel(data) {
  const headers = [
    "Código",
    "CNPJ",
    "Empresa",
    "Problema",
    "Método Autenticação",
    "Tem Login/Senha",
    "Tem Certificado",
    "Validade Certificado",
    "Último Erro",
    "Data Último Erro",
    "Último Login OK",
  ];

  const allRows = [
    ...(data.certInvalido || []),
    ...(data.semCredenciais || []),
    ...(data.loginInvalido || []),
  ];

  allRows.sort((a, b) => String(a.code || "").localeCompare(String(b.code || "")));

  const dataRows = allRows.map((r) => [
    r.code ?? "",
    r.cnpj ?? "",
    r.name ?? "",
    r.problema ?? "",
    r.authMethod ?? "",
    r.hasLogin ? "Sim" : "Não",
    r.hasCertificate ? "Sim" : "Não",
    r.certNotAfter ? formatCalendarDatePtBr(r.certNotAfter) : "—",
    r.lastLoginError ?? "",
    r.lastLoginErrorAt ? formatCalendarDatePtBr(r.lastLoginErrorAt) : "—",
    r.lastSuccessfulLogin ? formatCalendarDatePtBr(r.lastSuccessfulLogin) : "—",
  ]);

  const summaryRows = [
    [],
    ["Resumo"],
    ["Certificado inválido/vencido", data.totalCertInvalido ?? 0],
    ["Sem credenciais", data.totalSemCredenciais ?? 0],
    ["Login/senha inválido", data.totalLoginInvalido ?? 0],
    ["Total com problema", data.totalComProblema ?? 0],
    ["Total de empresas ativas", data.totalEmpresas ?? 0],
  ];

  const rows = [headers, ...dataRows, ...summaryRows];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Problemas de acesso");
  XLSX.writeFile(wb, "Empresas_problemas_acesso.xlsx");
}

function downloadNotasSemArquivoLocalExcel(data, competenciaApi) {
  const headers = [
    "Código",
    "Empresa",
    "Número da Nota",
    "Chave de Acesso",
    "Valor",
    "Tipo da Nota",
    "Data de Emissão",
    "Competência",
  ];
  const rows = [
    headers,
    ...(data.notes || []).map((n) => [
      n.companyCode ?? "",
      n.companyName ?? "",
      excelNumeroNota(n.numeroNota),
      n.accessKey ?? "",
      excelNumeric(n.valorNota),
      n.noteType ?? "",
      formatCalendarDatePtBr(n.issueDate),
      formatMonthYear(n.competenciaDate),
    ]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Notas sem arquivo local");
  const filename = `Notas_sem_arquivo_local_${competenciaApi.replace("-", "_")}.xlsx`;
  XLSX.writeFile(wb, filename);
}

// Rótulos pt-BR dos tipos de problema do relatório de integridade (mesmos do backend).
const INTEGRIDADE_TIPO_LABELS = {
  sem_certificado: "Sem certificado",
  cert_vencido: "Certificado vencido",
  coleta_desabilitada: "Coleta desabilitada",
  sem_coleta: "Sem coleta recente",
  coleta_falhou: "Coleta falhou",
  coleta_interrompida: "Coleta interrompida",
  erro_documento: "Erro de documento",
  sem_arquivo: "Sem arquivo XML",
  sem_metadados: "Sem metadados",
  mal_arquivada: "Mal arquivada",
  diferenca_portal: "Diferença com Portal",
  portal_sem_contagem: "Portal sem contagem",
};
const integridadeTipoLabel = (tipo) => INTEGRIDADE_TIPO_LABELS[tipo] || tipo;

// Planilha do relatório de integridade: aba "100% OK" + uma aba por tipo de problema.
function downloadIntegridadeColetaExcel(data, competenciaMmYyyy) {
  const baseRow = (e) => ({
    Código: e.code || "",
    Empresa: e.name || "",
    CNPJ: e.cnpj || "",
    "Notas Hub": e.contadores?.notasHub ?? "",
    "Notas Portal": e.contadores?.notasPortal ?? "",
    "Diferença (Portal − Hub)": e.contadores?.diferenca ?? "",
  });

  const wb = XLSX.utils.book_new();

  const okRows = (data.empresas || []).filter((e) => e.ok).map(baseRow);
  const wsOk = okRows.length
    ? XLSX.utils.json_to_sheet(okRows)
    : XLSX.utils.aoa_to_sheet([
        ["Código", "Empresa", "CNPJ", "Notas Hub", "Notas Portal", "Diferença (Portal − Hub)"],
      ]);
  XLSX.utils.book_append_sheet(wb, wsOk, "100% OK");

  for (const tipo of Object.keys(data.totais?.porTipo || {})) {
    const rows = [];
    for (const e of data.empresas || []) {
      for (const p of e.problemas || []) {
        if (p.tipo !== tipo) continue;
        rows.push({ ...baseRow(e), Quantidade: p.quantidade, Detalhe: p.detalhe });
      }
    }
    if (!rows.length) continue;
    const ws = XLSX.utils.json_to_sheet(rows);
    const sheetName = integridadeTipoLabel(tipo)
      .replace(/[\\/?*[\]:]/g, "")
      .slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  }

  XLSX.writeFile(wb, `Integridade_coleta_${competenciaMmYyyy}.xlsx`);
}

const PARAM_GRUPO_EMPRESAS = {
  text: "Grupo de empresas",
  type: "Todos / Asescont / Comsind / Advisa",
  key: "grupo",
  inputType: "grupo",
  required: false,
};

const PARAM_STATUS_NOTA = {
  text: "Situação da nota",
  type: "Normal / Cancelada / Substituída (seleção múltipla; vazio = todas)",
  key: "status",
  inputType: "status",
  required: false,
};

const REPORT_DEFINITIONS = [
  {
    id: "conferencia-hub-portal-notas",
    name: "Conferência Hub × Portal (por período)",
    icon: "file-invoice",
    params: [
      {
        arg: 1,
        text: "*Data inicial",
        type: "Data (aaaa-mm-dd)",
        key: "date_from",
        inputType: "date",
        required: true,
      },
      {
        arg: 2,
        text: "*Data final",
        type: "Data (aaaa-mm-dd)",
        key: "date_to",
        inputType: "date",
        required: true,
      },
      { arg: 3, ...PARAM_GRUPO_EMPRESAS },
    ],
  },
  {
    id: "integridade-coleta",
    name: "Integridade da coleta (por competência)",
    icon: "file-invoice",
    params: [
      {
        arg: 1,
        text: "*Competência",
        type: "mm/aaaa",
        key: "competencia",
        inputType: "competencia",
        required: true,
      },
      { arg: 2, ...PARAM_GRUPO_EMPRESAS },
    ],
  },
  {
    id: "numero-notas-todas-empresas",
    name: "Número de notas (Todas as Empresas)",
    icon: "file-invoice",
    params: [
      {
        arg: 1,
        text: "*Competência",
        type: "mm/aaaa",
        key: "competencia",
        inputType: "competencia",
        required: true,
      },
      { arg: 2, ...PARAM_GRUPO_EMPRESAS },
    ],
  },
  {
    id: "valor-liquido-todas-empresas",
    name: "Valor líquido (Todas as Empresas)",
    icon: "file-invoice-dollar",
    params: [
      {
        arg: 1,
        text: "*Competência",
        type: "mm/aaaa",
        key: "competencia",
        inputType: "competencia",
        required: true,
      },
      {
        arg: 2,
        text: "Empresa",
        type: "Código(s) ou *",
        key: "empresa",
        inputType: "empresa",
        required: false,
      },
      { arg: 3, ...PARAM_GRUPO_EMPRESAS },
    ],
  },
  {
    id: "relacao-retencoes",
    name: "Totalizador e Retenções",
    icon: "file-invoice",
    params: [
      {
        arg: 1,
        text: "*Competência inicial",
        type: "mm/aaaa",
        key: "competencia_inicial",
        inputType: "competencia",
        required: true,
      },
      {
        arg: 2,
        text: "*Competência final",
        type: "mm/aaaa",
        key: "competencia_final",
        inputType: "competencia",
        required: true,
      },
      {
        arg: 3,
        text: "*Empresa",
        type: "Códigos separados por ',' ou  * para todas",
        key: "empresa",
        inputType: "empresa",
        required: false,
      },
      {
        arg: 4,
        text: "Prestados ou Tomados",
        type: "Todos / Prestados / Tomados",
        key: "tipo",
        inputType: "tipo",
        required: false,
      },
      { arg: 5, ...PARAM_GRUPO_EMPRESAS },
      { arg: 6, ...PARAM_STATUS_NOTA },
    ],
  },
  {
    id: "relacao-retencoes-por-grupo",
    name: "Retenções e Totalizadores por Grupo",
    icon: "file-invoice",
    params: [
      {
        arg: 1,
        text: "*Competência inicial",
        type: "mm/aaaa",
        key: "competencia_inicial",
        inputType: "competencia",
        required: true,
      },
      {
        arg: 2,
        text: "*Competência final",
        type: "mm/aaaa",
        key: "competencia_final",
        inputType: "competencia",
        required: true,
      },
      {
        arg: 3,
        text: "Prestados ou Tomados",
        type: "Todos / Prestados / Tomados",
        key: "tipo",
        inputType: "tipo",
        required: false,
      },
      { arg: 4, ...PARAM_GRUPO_EMPRESAS },
      { arg: 5, ...PARAM_STATUS_NOTA },
    ],
  },
  {
    id: "relacao-notas-divergentes",
    name: "Relação de Notas Divergentes",
    icon: "exclamation-triangle",
    params: [
      {
        arg: 1,
        text: "*Emissão inicial",
        type: "Data (dd/mm/aaaa)",
        key: "date_from",
        inputType: "date",
        required: true,
      },
      {
        arg: 2,
        text: "*Emissão final",
        type: "Data (dd/mm/aaaa)",
        key: "date_to",
        inputType: "date",
        required: true,
      },
      {
        arg: 3,
        text: "Empresa",
        type: "Código(s) ou *",
        key: "empresa",
        inputType: "empresa",
        required: false,
      },
      {
        arg: 4,
        text: "Prestados ou Tomados",
        type: "Todos / Prestados / Tomados",
        key: "tipo",
        inputType: "tipo",
        required: false,
      },
      { arg: 5, ...PARAM_GRUPO_EMPRESAS },
      { arg: 6, ...PARAM_STATUS_NOTA },
    ],
  },
  {
    id: "conferencia-chaves-hub-dominio",
    name: "Conferência chaves Hub × Domínio",
    icon: "key",
    params: [
      {
        arg: 1,
        text: "*Competencia inicial",
        type: "mm/aaaa",
        key: "competencia_inicial",
        inputType: "competencia",
        required: true,
      },
      {
        arg: 2,
        text: "*Competencia final",
        type: "mm/aaaa",
        key: "competencia_final",
        inputType: "competencia",
        required: true,
      },
      {
        arg: 3,
        text: "*Empresa",
        type: "Codigos separados por ',' ou  * para todas",
        key: "empresa",
        inputType: "empresa",
        required: false,
      },
      {
        arg: 4,
        text: "Prestados ou Tomados",
        type: "Todos / Prestados / Tomados",
        key: "tipo",
        inputType: "tipo",
        required: false,
      },
      { arg: 5, ...PARAM_GRUPO_EMPRESAS },
    ],
  },
  {
    id: "notas-sem-arquivo-local",
    name: "Notas ausentes localmente",
    icon: "file-excel",
    params: [
      {
        arg: 1,
        text: "*Competência",
        type: "mm/aaaa",
        key: "competencia",
        inputType: "competencia",
        required: true,
      },
    ],
  },
  {
    id: "empresas-nao-cadastradas",
    name: "Empresas ativas no Domínio não cadastradas no Hub",
    icon: "building",
    params: [
      { arg: 1, ...PARAM_GRUPO_EMPRESAS },
    ],
  },
  {
    id: "empresas-problemas-acesso",
    name: "Empresas com problemas de acesso ao portal",
    icon: "exclamation-circle",
    params: [
      { arg: 1, ...PARAM_GRUPO_EMPRESAS },
    ],
  },
  {
    id: "certificados",
    name: "Certificados (vencidos/a vencer + sem certificado)",
    icon: "certificate",
    params: [
      {
        arg: 1,
        text: "Horizonte de aviso (dias)",
        type: "Número (padrão 30)",
        key: "warn_days",
        inputType: "text",
        required: false,
      },
      { arg: 2, ...PARAM_GRUPO_EMPRESAS },
    ],
  },
];

export default function Relatorios() {
  const [selectedReportId, setSelectedReportId] = useState(
    REPORT_DEFINITIONS[0]?.id || "",
  );
  const [paramValues, setParamValues] = useState({});
  const [reportSuccessMessage, setReportSuccessMessage] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [certResult, setCertResult] = useState(null);
  const [certExcelLoading, setCertExcelLoading] = useState(false);

  const selectedReport = REPORT_DEFINITIONS.find(
    (r) => r.id === selectedReportId,
  );
  const displayParams = selectedReport
    ? [...selectedReport.params].sort((a, b) => {
        if (a.key === "grupo" && b.key === "empresa") return -1;
        if (a.key === "empresa" && b.key === "grupo") return 1;
        return a.arg - b.arg;
      })
    : [];

  const setParam = (key, value) => {
    setParamValues((prev) => ({ ...prev, [key]: value }));
    setReportError(null);
  };

  const runReport = async () => {
    if (!selectedReport) return;

    const isDateRangeReport =
      selectedReport.id === "relacao-notas-divergentes" ||
      selectedReport.id === "conferencia-hub-portal-notas";
    if (isDateRangeReport) {
      const dateFrom = (paramValues.date_from || "").trim();
      const dateTo = (paramValues.date_to || "").trim();
      if (!dateFrom || !dateTo) {
        setReportError("Informe as datas de emissão inicial e final.");
        return;
      }
      if (new Date(dateFrom) > new Date(dateTo)) {
        setReportError("Data inicial não pode ser maior que a data final.");
        return;
      }
    } else if (
      selectedReport.id === "relacao-retencoes" ||
      selectedReport.id === "relacao-retencoes-por-grupo" ||
      selectedReport.id === "conferencia-chaves-hub-dominio"
    ) {
      const competenciaInicialInput = (
        paramValues.competencia_inicial || ""
      ).trim();
      const competenciaFinalInput = (
        paramValues.competencia_final || ""
      ).trim();

      if (!competenciaInicialInput || !competenciaFinalInput) {
        setReportError(
          "Informe a competência inicial e a competência final (mm/aaaa).",
        );
        return;
      }

      const competenciaInicialApi = competenciaMmAaaaToApi(
        competenciaInicialInput,
      );
      const competenciaFinalApi = competenciaMmAaaaToApi(competenciaFinalInput);

      if (!competenciaInicialApi || !competenciaFinalApi) {
        setReportError("Competências inválidas. Use o formato mm/aaaa.");
        return;
      }

      if (competenciaInicialApi > competenciaFinalApi) {
        setReportError(
          "Competência inicial não pode ser maior que a competência final.",
        );
        return;
      }
    } else if (
      selectedReport.id !== "relacao-retencoes" &&
      selectedReport.id !== "relacao-retencoes-por-grupo" &&
      selectedReport.id !== "conferencia-chaves-hub-dominio" &&
      selectedReport.id !== "conferencia-hub-portal-notas" &&
      selectedReport.id !== "relacao-notas-divergentes" &&
      selectedReport.id !== "empresas-nao-cadastradas" &&
      selectedReport.id !== "empresas-problemas-acesso" &&
      selectedReport.id !== "certificados"
    ) {
      const competenciaInput = (paramValues.competencia || "").trim();
      if (!competenciaInput) {
        setReportError("Informe a competência (mm/aaaa).");
        return;
      }
      const competenciaApi = competenciaMmAaaaToApi(competenciaInput);
      if (!competenciaApi) {
        setReportError("Competência inválida. Use o formato mm/aaaa.");
        return;
      }
    }

    try {
      setReportLoading(true);
      setReportError(null);
      setReportSuccessMessage(null);

      const grupoVal = (paramValues.grupo || "").trim();
      const empresaVal = (paramValues.empresa || "").trim() || "*";
      if (grupoVal && grupoVal !== "*" && empresaVal !== "*") {
        setReportError(
          "Não preencha Empresa e Grupo de empresas ao mesmo tempo.",
        );
        return;
      }

      let res;
      if (selectedReport.id === "numero-notas-todas-empresas") {
        const competenciaApi = competenciaMmAaaaToApi(
          (paramValues.competencia || "").trim(),
        );
        res = await reportsAPI.numeroNotasTodasEmpresas(
          competenciaApi,
          grupoVal,
        );
      } else if (selectedReport.id === "valor-liquido-todas-empresas") {
        const competenciaApi = competenciaMmAaaaToApi(
          (paramValues.competencia || "").trim(),
        );
        res = await reportsAPI.valorLiquidoTodasEmpresas(
          competenciaApi,
          empresaVal,
          grupoVal,
        );
      } else if (selectedReport.id === "relacao-retencoes") {
        res = await reportsAPI.relacaoRetencoes(
          competenciaMmAaaaToApi(
            (paramValues.competencia_inicial || "").trim(),
          ),
          competenciaMmAaaaToApi((paramValues.competencia_final || "").trim()),
          empresaVal,
          (paramValues.tipo || "").trim(),
          grupoVal,
          (paramValues.status || []).join(","),
        );
      } else if (selectedReport.id === "relacao-retencoes-por-grupo") {
        res = await reportsAPI.relacaoRetencoesPorGrupo(
          competenciaMmAaaaToApi(
            (paramValues.competencia_inicial || "").trim(),
          ),
          competenciaMmAaaaToApi((paramValues.competencia_final || "").trim()),
          (paramValues.tipo || "").trim(),
          "*",
          grupoVal,
          (paramValues.status || []).join(","),
        );
      } else if (selectedReport.id === "conferencia-chaves-hub-dominio") {
        res = await reportsAPI.conferenciaChavesHubDominio(
          competenciaMmAaaaToApi(
            (paramValues.competencia_inicial || "").trim(),
          ),
          competenciaMmAaaaToApi((paramValues.competencia_final || "").trim()),
          empresaVal,
          (paramValues.tipo || "").trim(),
          grupoVal,
        );
      } else if (selectedReport.id === "relacao-notas-divergentes") {
        res = await reportsAPI.relacaoNotasDivergentes(
          (paramValues.date_from || "").trim(),
          (paramValues.date_to || "").trim(),
          empresaVal,
          (paramValues.tipo || "").trim(),
          grupoVal,
          (paramValues.status || []).join(","),
        );
      } else if (selectedReport.id === "conferencia-hub-portal-notas") {
        res = await reportsAPI.conferenciaHubPortalNotas(
          (paramValues.date_from || "").trim(),
          (paramValues.date_to || "").trim(),
          grupoVal,
        );
      } else if (selectedReport.id === "notas-sem-arquivo-local") {
        const competenciaApi = competenciaMmAaaaToApi(
          (paramValues.competencia || "").trim(),
        );
        res = await reportsAPI.notasSemArquivoLocal(competenciaApi);
      } else if (selectedReport.id === "integridade-coleta") {
        const competenciaMmYyyy = competenciaMmAaaaToMmYyyy(
          (paramValues.competencia || "").trim(),
        );
        res = await reportsAPI.integridadeColeta(competenciaMmYyyy, grupoVal);
      } else if (selectedReport.id === "empresas-nao-cadastradas") {
        res = await reportsAPI.empresasNaoCadastradas(grupoVal);
      } else if (selectedReport.id === "empresas-problemas-acesso") {
        res = await reportsAPI.empresasProblemasAcesso(grupoVal);
      } else if (selectedReport.id === "certificados") {
        res = await reportsAPI.certificados(
          grupoVal,
          (paramValues.warn_days || "").trim(),
        );
      }

      if (res?.success && res?.data) {
        if (selectedReport.id === "certificados") {
          setCertResult(res.data);
        }
        if (selectedReport.id === "numero-notas-todas-empresas") {
          const competenciaApi = competenciaMmAaaaToApi(
            (paramValues.competencia || "").trim(),
          );
          downloadNumeroNotasExcel(res.data, competenciaApi);
        }
        if (selectedReport.id === "valor-liquido-todas-empresas") {
          const competenciaApi = competenciaMmAaaaToApi(
            (paramValues.competencia || "").trim(),
          );
          downloadValorLiquidoExcel(res.data, competenciaApi);
        }
        if (selectedReport.id === "relacao-retencoes") {
          const competenciaInicialApi = competenciaMmAaaaToApi(
            (paramValues.competencia_inicial || "").trim(),
          );
          const competenciaFinalApi = competenciaMmAaaaToApi(
            (paramValues.competencia_final || "").trim(),
          );
          downloadRelacaoRetencoesExcel(
            res.data,
            competenciaInicialApi,
            competenciaFinalApi,
          );
        }
        if (selectedReport.id === "relacao-retencoes-por-grupo") {
          const competenciaInicialApi = competenciaMmAaaaToApi(
            (paramValues.competencia_inicial || "").trim(),
          );
          const competenciaFinalApi = competenciaMmAaaaToApi(
            (paramValues.competencia_final || "").trim(),
          );
          downloadRelacaoRetencoesPorGrupoExcel(
            res.data,
            competenciaInicialApi,
            competenciaFinalApi,
          );
        }
        if (selectedReport.id === "conferencia-chaves-hub-dominio") {
          const competenciaInicialApi = competenciaMmAaaaToApi(
            (paramValues.competencia_inicial || "").trim(),
          );
          const competenciaFinalApi = competenciaMmAaaaToApi(
            (paramValues.competencia_final || "").trim(),
          );
          downloadConferenciaChavesExcel(
            res.data,
            competenciaInicialApi,
            competenciaFinalApi,
          );
        }
        if (selectedReport.id === "relacao-notas-divergentes")
          downloadRelacaoNotasDivergentesExcel(res.data);
        if (selectedReport.id === "conferencia-hub-portal-notas") {
          downloadConferenciaHubPortalExcel(res.data);
        }
        if (selectedReport.id === "notas-sem-arquivo-local") {
          const competenciaApi = competenciaMmAaaaToApi(
            (paramValues.competencia || "").trim(),
          );
          downloadNotasSemArquivoLocalExcel(res.data, competenciaApi);
        }
        if (selectedReport.id === "integridade-coleta") {
          const competenciaMmYyyy = competenciaMmAaaaToMmYyyy(
            (paramValues.competencia || "").trim(),
          );
          downloadIntegridadeColetaExcel(res.data, competenciaMmYyyy);
        }
        if (selectedReport.id === "empresas-nao-cadastradas") {
          downloadEmpresasNaoCadastradasExcel(res.data);
        }
        if (selectedReport.id === "empresas-problemas-acesso") {
          downloadEmpresasProblemasAcessoExcel(res.data);
        }

        setReportSuccessMessage(`Relatório gerado com sucesso!`);
      } else {
        setReportError(res?.message || "Erro ao gerar relatório.");
      }
    } catch (err) {
      setReportError(err.message || "Erro ao gerar relatório.");
    } finally {
      setReportLoading(false);
    }
  };

  const baixarCertExcel = async () => {
    setCertExcelLoading(true);
    setReportError(null);
    try {
      const grupoVal = (paramValues.grupo || "").trim();
      const warnVal = (paramValues.warn_days || "").trim();
      const r = await reportsAPI.certificadosExcel(grupoVal, warnVal);
      if (!r?.success) {
        setReportError(r?.message || "Erro ao baixar a planilha de certificados.");
      }
    } catch (err) {
      setReportError(err.message || "Erro ao baixar a planilha de certificados.");
    } finally {
      setCertExcelLoading(false);
    }
  };

  const renderCertTable = (titulo, rows, showValidade) => {
    const columns = [
      {
        key: "code",
        header: "Código",
        width: pixel(90),
        renderCell: (r) => <Text size="sm">{r.code}</Text>,
      },
      {
        key: "name",
        header: "Empresa",
        width: proportional(3),
        renderCell: (r) => <Text size="sm">{r.name}</Text>,
      },
      {
        key: "cnpj",
        header: "CNPJ",
        width: proportional(2),
        renderCell: (r) => <Text size="sm">{r.cnpj}</Text>,
      },
      ...(showValidade
        ? [
            {
              key: "not_after",
              header: "Validade (fim)",
              width: pixel(130),
              renderCell: (r) => (
                <Text size="sm">
                  {r.not_after
                    ? new Date(r.not_after).toLocaleDateString("pt-BR")
                    : "—"}
                </Text>
              ),
            },
          ]
        : []),
    ];

    return (
      <Card padding={0}>
        <Stack gap={0}>
          <HStack gap={2} vAlign="center" hAlign="between" padding={3}>
            <Text type="label" color="secondary">
              {titulo}
            </Text>
            <Text type="supporting">{rows.length}</Text>
          </HStack>
          {rows.length === 0 ? (
            <Stack padding={3}>
              <Text type="supporting" justify="center">
                Nenhuma empresa.
              </Text>
            </Stack>
          ) : (
            <Stack isScrollable height={rows.length > 6 ? 288 : undefined}>
              <Table
                data={rows.map((r, i) => ({ ...r, _rowId: `${r.code}-${i}` }))}
                columns={columns}
                idKey="_rowId"
                density="compact"
                hasHover
              />
            </Stack>
          )}
        </Stack>
      </Card>
    );
  };

  // Renderiza o controle de entrada apropriado para cada parâmetro do relatório
  const renderParamInput = (param) => {
    if (param.inputType === "tipo") {
      return (
        <Selector
          label={param.text}
          description={param.type}
          options={TIPO_OPTIONS}
          value={paramValues[param.key] || ""}
          onChange={(v) => setParam(param.key, v)}
        />
      );
    }
    if (param.inputType === "status") {
      return (
        <MultiSelector
          label={param.text}
          description={param.type}
          options={STATUS_NOTA_OPTIONS}
          value={paramValues[param.key] || []}
          onChange={(v) => setParam(param.key, v)}
          hasSelectAll
          selectAllLabel="Todas"
          placeholder="Todas"
        />
      );
    }
    if (param.inputType === "grupo") {
      return (
        <Selector
          label={param.text}
          description={param.type}
          options={GRUPO_OPTIONS}
          value={paramValues[param.key] || ""}
          onChange={(v) => {
            setParam(param.key, v);
            if (v) setParam("empresa", "");
          }}
        />
      );
    }
    if (param.inputType === "date") {
      return (
        <DateInput
          label={param.text}
          description={param.type}
          isRequired={param.required}
          value={paramValues[param.key] || ""}
          onChange={(v) => setParam(param.key, v || "")}
        />
      );
    }
    const empresaDisabled =
      param.key === "empresa" && (paramValues.grupo || "").trim() !== "";
    return (
      <TextInput
        label={param.text}
        description={param.type}
        isRequired={param.required}
        isDisabled={empresaDisabled}
        placeholder={
          param.inputType === "competencia" ? "mm/aaaa" : "* para todos"
        }
        value={paramValues[param.key] || ""}
        onChange={(v) => setParam(param.key, v)}
      />
    );
  };

  const certCards =
    selectedReport?.id === "certificados" && certResult
      ? [
          {
            label: "Vencidos",
            val: certResult.counts.expired,
            variant: "error",
          },
          {
            label: `A vencer (≤ ${certResult.warnDays}d)`,
            val: certResult.counts.expiring,
            variant: "warning",
          },
          {
            label: "Sem cert (login OK)",
            val: certResult.counts.comLogin,
            variant: "info",
          },
          {
            label: "Sem cert (sem login OK)",
            val: certResult.counts.semNada,
            variant: "neutral",
          },
        ]
      : [];

  return (
    <AstryxTheme>
      <Stack
        as="section"
        aria-label="Relatórios Operacionais"
        gap={4}
        width="100%"
      >
        <Stack gap={0.5}>
          <Heading level={2}>Relatórios Operacionais</Heading>
          <Text type="supporting">
            Configure os parâmetros abaixo para exportar os dados em Excel.
          </Text>
        </Stack>

        <HStack gap={4} vAlign="stretch" wrap="wrap">
          {/* Sidebar: lista de relatórios */}
          <Stack width={320} gap={2}>
            <Text type="label" color="secondary">
              Relatórios Disponíveis
            </Text>
            <Stack gap={2}>
              {REPORT_DEFINITIONS.map((report) => (
                <SelectableCard
                  key={report.id}
                  label={report.name}
                  isSelected={selectedReportId === report.id}
                  onChange={() => {
                    setSelectedReportId(report.id);
                    setReportSuccessMessage(null);
                    setReportError(null);
                    setParamValues({});
                    setCertResult(null);
                  }}
                  padding={3}
                >
                  <HStack gap={2} vAlign="center">
                    <Text
                      color={
                        selectedReportId === report.id ? "accent" : "secondary"
                      }
                    >
                      <FontAwesomeIcon icon={report.icon} fixedWidth />
                    </Text>
                    <Text weight="semibold" size="sm">
                      {report.name}
                    </Text>
                  </HStack>
                </SelectableCard>
              ))}
            </Stack>
          </Stack>

          {/* Main: parâmetros e ação */}
          <StackItem size="fill">
            <Stack gap={4}>
              {selectedReport ? (
                <Card padding={4}>
                  <Stack gap={3}>
                    <Heading level={3}>Parâmetros</Heading>
                    <Grid columns={{ minWidth: 240, max: 2 }} gap={3}>
                      {displayParams.map((param) => (
                        <div key={param.key}>{renderParamInput(param)}</div>
                      ))}
                    </Grid>

                    <HStack gap={3} vAlign="center" wrap="wrap">
                      <Button
                        label={
                          reportLoading
                            ? "Processando..."
                            : selectedReport.id === "certificados"
                              ? "Gerar Relatório"
                              : "Gerar Planilha Excel"
                        }
                        variant="primary"
                        icon={<FontAwesomeIcon icon="file-download" />}
                        isLoading={reportLoading}
                        onClick={runReport}
                      />
                    </HStack>

                    {reportError && (
                      <Banner status="error" title={reportError} />
                    )}
                    {reportSuccessMessage && (
                      <Banner status="success" title={reportSuccessMessage} />
                    )}
                  </Stack>
                </Card>
              ) : (
                <Card padding={6}>
                  <EmptyState
                    title="Selecione um relatório ao lado para começar"
                    icon={<FontAwesomeIcon icon="mouse-pointer" />}
                  />
                </Card>
              )}

              {selectedReport?.id === "certificados" && certResult && (
                <Stack gap={3}>
                  <Grid columns={{ minWidth: 160, max: 4 }} gap={3}>
                    {certCards.map((c) => (
                      <Card key={c.label} padding={4}>
                        <Stack gap={1}>
                          <Heading level={3} type="display-2">
                            {c.val}
                          </Heading>
                          <Badge variant={c.variant} label={c.label} />
                        </Stack>
                      </Card>
                    ))}
                  </Grid>

                  <HStack gap={2}>
                    <Button
                      label={certExcelLoading ? "Baixando..." : "Baixar Excel"}
                      variant="secondary"
                      icon={<FontAwesomeIcon icon="file-download" />}
                      isLoading={certExcelLoading}
                      onClick={baixarCertExcel}
                    />
                  </HStack>

                  {renderCertTable("Vencidos", certResult.expiry.expired, true)}
                  {renderCertTable("A vencer", certResult.expiry.expiring, true)}
                  {renderCertTable(
                    "Sem certificado — login autentica",
                    certResult.coverage.comLogin,
                    false,
                  )}
                  {renderCertTable(
                    "Sem certificado — sem login válido",
                    certResult.coverage.semNada,
                    false,
                  )}
                </Stack>
              )}
            </Stack>
          </StackItem>
        </HStack>
      </Stack>
    </AstryxTheme>
  );
}
